"use client";
import { Button, InputField, Modal } from "@/components/ui";
import GaggleChatModal from "@/components/ui/GaggleModal";
import { useAppDispatch } from "@/hooks/reduxTypedHooks";
import { useAutoTranslate } from "@/lib/i18n/useAutoTranslate";
import { useNotification } from "@/lib/NotificationProvider";
import { useTriggerResponseMutation } from "@/store/services";
import { resetCrisis } from "@/store/slice/crisisSlice";
import { clearTier2CrisisCookie } from "@/utils/crisisCookie";
import { isGagglePopupDisabled } from "@/utils/gaggle";
import { PhoneOutlined } from "@ant-design/icons";
import { Divider, Space, Spin } from "antd";
import { useFormik } from "formik";

import { useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { IoSend } from "react-icons/io5";
import { crisisModalScheme } from "./helpers";
interface CrisisSupportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  id?: string;
  type?: "chat" | "wellness" | string | null;
  tier?: "1" | "2" | null;
}

const CrisisSupportModal = ({
  open,
  setOpen,
  id,
  type,
  tier,
}: CrisisSupportModalProps) => {
  const tr = useAutoTranslate();
  const gagglePopupDisabled = isGagglePopupDisabled();
  const { notifyError, notifySuccess } = useNotification();
  const [triggerResponse] = useTriggerResponseMutation();
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  const [gaggleChatOpen, setGaggleChatOpen] = useState(false);
  const [shouldText, setShouldText] = useState(false);
  const severityLabel = tier === "2" ? "Distress" : "Wellness";
  const severityColor = tier === "2" ? "text-red-600" : "text-amber-500";
  const severityIconColor = tier === "2" ? "red" : "#F59E0B";
  const dispatch = useAppDispatch();
  const closeModal = () => {
    clearTier2CrisisCookie();
    setOpen(false);
    dispatch(resetCrisis());
    formik.resetForm();
  };
  const formik = useFormik({
    initialValues: { safeText: "" },
    validationSchema: crisisModalScheme,
    onSubmit: async (values) => {
      await handleResponse(values.safeText.trim(), "");
    },
  });

  const submitSafeAcknowledgement = async () => {
    formik.setFieldTouched("safeText", true, true);
    const errors = await formik.validateForm();

    if (errors.safeText) {
      return;
    }

    await formik.submitForm();
  };

  // :point_right: Helper for calling, texting, or opening a chat link
  const handlePhoneAction = (
    action: "call" | "text" | "link",
    target: string,
  ) => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (action === "link") {
      window.open(target, "_blank");
      return;
    }
    if (isMobile) {
      if (action === "call") {
        window.location.href = `tel:${target}`;
      } else if (action === "text") {
        window.location.href = `sms:${target}`;
      }
    } else {
      alert(
        tr("Non-mobile device detected. Cannot perform call or text action."),
      );
    }
  };
  // Handle API response and notifications
  const handleResponse = async (
    responseText: string,
    successDescription: string,
  ) => {
    if (!id) {
      console.log(tr("Missing entity ID."));
      return;
    }
    setLoadingButton(responseText);
    try {
      await triggerResponse({
        entityId: id,
        body: {
          response: responseText,
          type: type || "",
        },
      }).unwrap();

      closeModal();
      if (responseText === "Call the California Youth Crisis Line (24/7)") {
        notifySuccess({
          message: tr("Success"),
          description: successDescription,
        });
      }
    } catch {
      notifyError({
        message: tr("Failed"),
        description: tr(
          "Something went wrong while sending your response. Please try again.",
        ),
      });
    } finally {
      setLoadingButton(null);
    }
  };
  return (
    <>
      <div className="crisis-support-modal">
        <Modal
          open={open}
          width={800}
          className="gaggle-chat-modal crisis-support-modal"
          onClose={() => {}}
          maskClosable={false}
          closeIcon={null}
        >
          <div className="mx-auto w-full max-w-[640px] px-3 py-4 text-center md:px-5 md:py-6">
            {/* Header */}
            <div className="flex items-center justify-center gap-2 text-lg md:text-2xl">
              <FiAlertTriangle color={severityIconColor} />
              <div className={`font-bold ${severityColor}`}>
                {tr(severityLabel)}
              </div>
            </div>
            {/* Message */}
            <div className="mx-auto mt-2 max-w-[560px] text-pretty text-sm leading-6 text-gray-600 md:text-base">
              {tier === "2"
                ? tr(
                    "It sounds like you might be in danger of hurting yourself or someone else. Help is available right now.",
                  )
                : tr(
                    "It looks like you may be going through a hard time. You’re not alone. Would you like to talk to someone right now?",
                  )}
            </div>
            {!gagglePopupDisabled ? (
              <>
                <div className="mt-5 w-full text-left">
                  <div className="relative overflow-hidden rounded-[24px] border border-rose-100 bg-gradient-to-br from-rose-50 via-amber-50 to-orange-50 p-4 text-gray-900 shadow-lg md:rounded-3xl md:p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        </span>
                        {tr("LIVE COUNSELOR ONLINE")}
                      </span>
                    </div>

                    <div className="mt-4 rounded-2xl border border-white/70 bg-white/90 p-3 shadow-inner md:p-4">
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2 text-xl font-bold leading-tight text-gray-900 md:text-4xl">
                          {tr("Talk with a live counselor now 🥺")}
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-900 md:text-sm">
                            {tr("Real humans — not bots.")}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 overflow-hidden rounded-2xl">
                        <GaggleChatModal
                          open={gaggleChatOpen}
                          onClose={() => {
                            setGaggleChatOpen(false);
                            closeModal();
                          }}
                          setShouldText={setShouldText}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <Divider
                  style={{
                    borderColor: "#f0f2f5",
                    color: "#667185",
                  }}
                  className="!text-xs xs:!text-base"
                  size="small"
                >
                  {tr("OR")}
                </Divider>
              </>
            ) : null}
            {/* Buttons */}
            <Space direction="vertical" className="mt-2 w-full space-y-2">
              {/* CALL: 1-888-3005220 */}
              <Button
                type="primary"
                block
                icon={<PhoneOutlined />}
                className="mx-auto !h-auto !w-full !whitespace-normal !px-4 !py-3 !text-xs !leading-5 md:!w-full md:!text-base"
                loading={
                  loadingButton ===
                  "Call the California Youth Crisis Line (24/7)"
                }
                onClick={() => {
                  handleResponse(
                    "Call the California Youth Crisis Line (24/7)",
                    tr("We're here to support you."),
                  );
                  handlePhoneAction("call", "1-888-3005220");
                }}
              >
                {tr("Call a Gaggle Counselor Now: 1-888-300-5220")}
              </Button>
              {/* CHAT LINK */}
              {/* CANCEL / Not right now */}
              {/* <Button
                loading={
                  loadingButton === "Not right now" ||
                  (shouldText &&
                    loadingButton === "Text the California Youth Crisis Line")
                }
                block
                onClick={() =>
                  handleResponse(
                    shouldText
                      ? "Text the California Youth Crisis Line"
                      : "Not right now",
                    ""
                  )
                }
                className="md:!w-1/2 md:min-w-[500px]"
              >
                I’m Safe Right Now
              </Button> */}
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitSafeAcknowledgement();
                }}
                className="mt-1 flex w-full justify-center"
              >
                <div className="w-full max-w-[520px] text-left">
                  <div className="mb-2 text-center text-xs leading-5 text-[#475467] md:text-sm">
                    {tr('To close this alert, type a message that includes the word "safe".')}
                  </div>
                  <InputField
                    type="text"
                    name="safeText"
                    placeholder={tr(
                      'Type "I am safe right now" (or "I am safe now") to continue',
                    )}
                    value={formik.values.safeText}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.safeText}
                    suffix={
                      loadingButton !== null ? (
                        <Spin size="small" />
                      ) : (
                        <IoSend
                          className="cursor-pointer"
                          color="#284574"
                          onClick={() => {
                            void submitSafeAcknowledgement();
                          }}
                        />
                      )
                    }
                    error={
                      formik.touched.safeText && Boolean(formik.errors.safeText)
                    }
                    errorMessage={formik.errors.safeText}
                    disabled={loadingButton !== null}
                  />
                </div>
              </form>
            </Space>
          </div>
        </Modal>
      </div>
    </>
  );
};
export default CrisisSupportModal;
